import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { getServices, createService, updateService, deleteService } from '../services/api';
import './ListPage.css';

// M2/M3 (NER-1386): servicios ofrecidos (duración + precio) con CRUD contra los
// write endpoints (NER-1475). durationMin entero > 0; priceArs número >= 0.
const EMPTY_FORM = { name: '', durationMin: '', priceArs: '' };

const formatPrice = (priceArs) => {
  if (priceArs == null || priceArs === 0) return null;
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    }).format(priceArs);
  } catch {
    return `$${priceArs}`;
  }
};

function ServicesPage() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // form modal (create / edit)
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getServices();
      setServices(res?.services || []);
    } catch (err) {
      console.error('Error loading services:', err);
      setServices([]);
      setError(t('services.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      durationMin: s.durationMin != null ? String(s.durationMin) : '',
      priceArs: s.priceArs != null ? String(s.priceArs) : '',
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setFormError(t('services.nameRequired'));
      return;
    }
    const durationMin = Number(form.durationMin);
    if (!Number.isInteger(durationMin) || durationMin <= 0) {
      setFormError(t('services.durationInvalid'));
      return;
    }
    const priceArs = form.priceArs === '' ? 0 : Number(form.priceArs);
    if (!Number.isFinite(priceArs) || priceArs < 0) {
      setFormError(t('services.priceInvalid'));
      return;
    }
    setSaving(true);
    setFormError('');
    const payload = { name, durationMin, priceArs };
    try {
      if (editing) {
        await updateService(editing.serviceId, payload);
      } else {
        await createService(payload);
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      console.error('Error saving service:', err);
      setFormError(t('services.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteService(deleteTarget.serviceId);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      console.error('Error deleting service:', err);
      setDeleteError(t('services.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="list-page">
      <div className="page-header">
        <h1>{t('services.title')}</h1>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} />
          {t('services.new')}
        </button>
      </div>
      <p className="list-page-subtitle">{t('services.subtitle')}</p>

      {error && <div className="error-message">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : services.length === 0 ? (
          <div className="empty-state">{t('services.empty')}</div>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('services.name')}</th>
                  <th>{t('services.duration')}</th>
                  <th>{t('services.price')}</th>
                  <th className="col-actions">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => {
                  const price = formatPrice(s.priceArs);
                  return (
                    <tr key={s.serviceId}>
                      <td>
                        {s.name && s.name.trim() ? (
                          s.name
                        ) : (
                          // NER-2220: ítems legacy/seed cargados directo a DDB pueden
                          // tener name vacío. El form de alta/edición nunca lo permite
                          // (valida required), pero estos rows existen en cargas reales:
                          // los mostramos con placeholder para que el admin los detecte
                          // y los re-guarde con nombre, en vez de renderizar una celda en blanco.
                          <span className="list-muted">{t('services.unnamed')}</span>
                        )}
                      </td>
                      <td>
                        {s.durationMin != null
                          ? `${s.durationMin} ${t('services.minutesShort')}`
                          : '—'}
                      </td>
                      <td>{price || <span className="list-muted">{t('services.free')}</span>}</td>
                      <td className="col-actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => openEdit(s)}
                          aria-label={t('common.edit')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setDeleteTarget(s);
                            setDeleteError('');
                          }}
                          aria-label={t('common.delete')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? t('services.editTitle') : t('services.newTitle')}</h2>
            </div>
            <form onSubmit={submitForm}>
              <div className="modal-body">
                {formError && <div className="error-message">{formError}</div>}
                <div className="form-group">
                  <label htmlFor="svc-name">{t('services.name')} *</label>
                  <input
                    id="svc-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={120}
                    autoFocus
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="svc-duration">{t('services.durationMin')} *</label>
                    <input
                      id="svc-duration"
                      type="number"
                      min="1"
                      step="1"
                      value={form.durationMin}
                      onChange={(e) => setForm((f) => ({ ...f, durationMin: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="svc-price">{t('services.priceArs')}</label>
                    <input
                      id="svc-price"
                      type="number"
                      min="0"
                      step="1"
                      value={form.priceArs}
                      onChange={(e) => setForm((f) => ({ ...f, priceArs: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeForm}
                  disabled={saving}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!deleting) setDeleteTarget(null);
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('services.deleteTitle')}</h2>
            </div>
            <div className="modal-body">
              {deleteError && <div className="error-message">{deleteError}</div>}
              <p>{t('services.deleteConfirm', { name: deleteTarget.name })}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? t('common.saving') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicesPage;
